import React, { useContext, useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import CustomButton from "../../components/CustomButton";
import { ProjectContext } from "../../contexts/ProjectContextProvider";
import { useFormik } from "formik";
import * as Yup from "yup";
import styles from "./ManuscriptValidator.module.css";
import dzStyles from "../../components/modals/UploadDropzone.module.css";
import InitiativeContextProvider, {
  InitiativeContext,
} from "../../contexts/InitiativeContextProvider";
import AutoCompleteDropdown from "../../components/Inputs/AutoCompleteDropdown";
import { NoteIcon, WarningIcon } from "../../components/Icons/common";
import CustomDropzone, { children } from "../../components/CustomDropzone";
import CustomSelectBox from "../../components/Inputs/CustomSelectBox";
import useUpload from "../../hooks/useUpload";

import ToggleSwitch from "../../components/Inputs/toggleSwitch/ToggleSwitch";
import { PAPER_FORMAT } from "../../constants/constant";
import { sendJournalSelectEmail } from "../../api/Api";
import { showToast } from "../../utils/toastUtils";
import { Alert } from "@mui/material";
import { debounce } from "lodash";

const DEBOUNCE_DELAY = 300; // 300ms delay
const MIN_SEARCH_LENGTH = 2; // Minimum characters before searching

function ManuscriptValidator() {
  const [projectId, setProjectId] = useState(null);
  const [searchParams] = useSearchParams();

  const projectIdFromUrl = searchParams.get("projectId");
  const workstreamIdFromUrl = searchParams.get("workstreamId");

  const autoConnectProject = projectIdFromUrl;
  const autoConnectWorkstream = workstreamIdFromUrl;
  useEffect(() => {
    if (autoConnectProject) {
      setProjectId(autoConnectProject);
    }
  }, [autoConnectProject]);

  return (
    <InitiativeContextProvider projectId={projectId}>
      <ManuscriptValidatorContent
        setProjectId={setProjectId}
        autoConnectProject={autoConnectProject}
        autoConnectWorkstream={autoConnectWorkstream}
      />
    </InitiativeContextProvider>
  );
}

// validation schema for the form
const validationSchema = Yup.object().shape({
  journal_id: Yup.number().nullable().required("Journal is required"),
  paper_type_id: Yup.number().nullable().required("Paper type is required"),
});

function ManuscriptValidatorContent({
  setProjectId,
  autoConnectProject,
  autoConnectWorkstream,
}) {
  const [selectedJournal, setSelectedJournal] = useState(null);
  const [isNewJournal, setIsNewJournal] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const [isConnectChecked, setIsConnectChecked] = useState(
    !!autoConnectProject
  );
  const navigate = useNavigate();

  const formik = useFormik({
    initialValues: {
      project_id: autoConnectProject || null,
      workStream_id: autoConnectWorkstream || null,
      journal_id: null,
      paper_type_id: null,
      auto_delete_file: false,
    },
    validationSchema: validationSchema,
    context: { isConnectChecked }, // Pass context for conditional validation
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true);
      
      // Manual validation for project connection
      if (isConnectChecked && !values.project_id) {
        formik.setFieldError("project_id", "Project is required when connecting to existing project");
        setSubmitting(false);
        return;
      }
      
      if (isConnectChecked && !values.workStream_id) {
        formik.setFieldError("workStream_id", "Workstream is required when connecting to existing project");
        setSubmitting(false);
        return;
      }
      
      setProjectId(values.project_id);
      await handleUpload(values);
      setSubmitting(false);
      console.log("Form submitted with values:", values);
    },
  });

  const {
    setUploadData,
    journals,
    paperTypes,
    loadPaperTypes,
    handleUpload,
    loadJournals,
    clearPaperTypes,
  } = useUpload(formik.values.workStream_id);

  // Create a debounced version of loadJournals
  const debouncedLoadJournals = useCallback(
    debounce(async (searchTerm) => {
      if (searchTerm.length >= MIN_SEARCH_LENGTH) {
        await loadJournals(searchTerm);
      }
    }, DEBOUNCE_DELAY),
    [loadJournals]
  );

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      debouncedLoadJournals.cancel();
    };
  }, [debouncedLoadJournals]);

  useEffect(() => {
    if (selectedJournal) {
      sendJournalSelectEmail(selectedJournal).then((response) => {
        console.log(response);
        if (response.status === 200) {
          // showToast(response.message, "success");
          console.log("Email sent successfully");
        }
      });
    }
  }, [selectedJournal]);

  useEffect(() => {
    if (autoConnectProject) {
      setIsConnectChecked(true);
      formik.setFieldValue("project_id", autoConnectProject);
      setProjectId(autoConnectProject);
    }
    if (autoConnectWorkstream) {
      formik.setFieldValue("workStream_id", autoConnectWorkstream);
    }
  }, [autoConnectProject, autoConnectWorkstream]);

  // Trigger validation when isConnectChecked changes
  useEffect(() => {
    // Clear project and workstream errors when unchecking
    if (!isConnectChecked) {
      formik.setFieldError("project_id", undefined);
      formik.setFieldError("workStream_id", undefined);
    }
    // Trigger validation to update the form state
    formik.validateForm();
  }, [isConnectChecked]);

  return (
    <div className="p-4 px-xl-5 position-relative">
      <h5 className="fw-bold">Upload Manuscript</h5>
      <p className="p2 text-secondary">
        Submit your manuscript for validation against journal guidelines
      </p>

      <form
        onSubmit={formik.handleSubmit}
        className="rounded-3 p-3 p-xl-4 overflow-hidden primary-box"
      >
        <div className="d-flex align-items-center gap-2 mb-2">
          <NoteIcon />
          <h6 className="bg-white mb-0">New Submission</h6>
          <CustomButton
            className="d-flex gap-2 ms-auto  "
            onClick={() => {
              navigate("/previous-uploads");
            }}
          >
            Previous Uploads
          </CustomButton>
        </div>
        <p className="p4 mb-3 text-secondary">
          Fill out the form below to upload your manuscript for review.
        </p>

        <div className="d-md-flex align-content-center gap-3 mb-4">
          <AutoCompleteDropdown
            label="Target Journal"
            name="journal_id"
            className={styles.dropdown}
            required
            placeholder="Select Journal"
            noOptionsText="No journals available"
            helperText="The journal you're planning to submit to"
            fullWidth
            options={journals}
            value={
              journals.find((j) => j.id === formik.values.journal_id) || null
            }
            onChange={(event, selectedOption) => {
              setIsNewJournal(Boolean(selectedOption?.unique_id));
              setShowPopup(Boolean(selectedOption?.unique_id));
              formik.setFieldValue("journal_id", selectedOption?.id || null);
              //load paper types when journal is selected and clear when no journal is selected
              if (selectedOption?.id) {
                loadPaperTypes(selectedOption?.id || null);
              } else {
                clearPaperTypes();
              }
            }}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            onInputChange={(_, inputValue) => {
              const trimmedValue = inputValue.trim();
              if (trimmedValue.length >= MIN_SEARCH_LENGTH) {
                debouncedLoadJournals(trimmedValue);
              } else if (trimmedValue.length === 0) {
                // Load initial journals when input is cleared
                loadJournals();
              }
            }}
            error={formik.touched.journal_id && formik.errors.journal_id}
          />

          <AutoCompleteDropdown
            label="Paper Type"
            name="paper_type_id"
            className={styles.dropdown}
            required
            placeholder="Select Paper Type"
            noOptionsText="No paper types available"
            helperText="The type of manuscript you're submitting "
            fullWidth
            options={paperTypes}
            value={
              formik.values.paper_type_id
                ? paperTypes.find((j) => j.id === formik.values.paper_type_id)
                : null
            }
            onChange={(event, selectedOption) => {
              formik.setFieldValue("paper_type_id", selectedOption?.id || null);
            }}
            error={
              formik.touched.paper_type_id ? formik.errors.paper_type_id : ""
            }
          />
        </div>

        {isNewJournal && showPopup && (
          <div className="mb-4">
            <Alert severity="warning">
              This journal is not yet supported. We are working on adding it
              soon. In the meantime, please choose another journal.
            </Alert>
          </div>
        )}

        {/* dropzone */}
        <div className="primary-box p-3 p-xl-4 rounded-4 overflow-hidden mb-4">
          <p className="p3 fw-medium">Upload Manuscript</p>
          <CustomDropzone
            className={`${dzStyles.dropzone} mb-2`}
            setUploadData={setUploadData}
            initUrl={`${process.env.REACT_APP_BACKEND_BASE_URL}/papers/upload/`}
            method="POST"
            destroyDep={[]}
            acceptedFiles={PAPER_FORMAT}
            children={children}
          />
          <p className="p4 text-secondary mb-0">
            Upload your manuscript as a PDF file
          </p>
        </div>

        <div className="primary-box p-3 p-xl-4 rounded-4 overflow-hidden">
          <ConnectToProject
            setProjectId={setProjectId}
            formik={formik}
            isConnectChecked={isConnectChecked}
            setIsConnectChecked={setIsConnectChecked}
            autoConnectProject={autoConnectProject}
          />
        </div>

        {/* need help */}
        <NeedMoreHelp />

        <div className="d-xl-flex align-items-between mt-4 mb-3 ps-4">
          <div className="flex-grow-1 d-flex align-items-center gap-2">
            <label
              htmlFor="toggle"
              style={{ cursor: "pointer" }}
              className="form-check-label p4 fw-medium text-secondary"
            >
              Destroy the uploaded manuscript once the analysis is completed?
            </label>
            <ToggleSwitch
              id="toggle"
              isOn={formik.values.auto_delete_file}
              handleToggle={() => {
                formik.setFieldValue(
                  "auto_delete_file",
                  !formik.values.auto_delete_file
                );
              }}
            />
          </div>
          <div className="d-flex justify-content-end gap-2 mt-3 mt-xl-0">
            <CustomButton
              className="px-3 py-2"
              type="submit"
              disabled={formik.isSubmitting || isNewJournal}
              onClick={() => {
                if (!formik.values.journal_id || !formik.values.paper_type_id) {
                  console.log("Manual validation failed");
                  showToast("Please select a suitable journal and paper type", "error");
                  return;
                }
              }}
            >
              Upload and Validate
            </CustomButton>
          </div>
        </div>
      </form>
    </div>
  );
}

export default ManuscriptValidator;

export const ConnectToProject = ({
  formik,
  setProjectId,
  isConnectChecked,
  setIsConnectChecked,
  autoConnectProject,
}) => {
  const { data: projectArray } = useContext(ProjectContext);
  const { data: workStreamArray } = useContext(InitiativeContext);

  return (
    <div className="container bg-white rounded px-0">
      <div className="d-flex align-items-center gap-2">
        <input
          type="checkbox"
          id="connect"
          className={`form-check-input rounded-5 mt-1 me-2`}
          checked={isConnectChecked}
          onChange={() => {
            const newChecked = !isConnectChecked;
            setIsConnectChecked(newChecked);

            if (!newChecked && !autoConnectProject) {
              formik.setFieldValue("project_id", null);
              formik.setFieldValue("workStream_id", null);
              setProjectId(null);
              // Clear errors when unchecking
              formik.setFieldError("project_id", undefined);
              formik.setFieldError("workStream_id", undefined);
            }
          }}
        />
        <div>
          <label
            htmlFor="connect"
            style={{ cursor: "pointer" }}
            className="form-check-label p3 fw-medium"
          >
            Connect an existing project
          </label>
          <p className="p4 text-secondary mb-0">
            Link this manuscript to one of your existing projects
          </p>
        </div>
      </div>

      {isConnectChecked && (
        <div className="border-start ps-4 mt-4">
          <div className="mb-2">
            <CustomSelectBox
              label="Select Project"
              id="project-select"
              options={projectArray}
              value={formik?.values?.project_id || ""}
              onChange={(e) => {
                formik.setFieldValue("workStream_id", "");
                formik.setFieldValue("project_id", e.target.value);
                setProjectId(e.target.value);
              }}
              fullWidth
              disabled={!isConnectChecked}
              error={isConnectChecked && formik.touched.project_id && formik.errors.project_id}
            />
          </div>

          <div className="mb-2">
            <CustomSelectBox
              label="WorkStream"
              id="workstream-id"
              options={workStreamArray?.initiatives || []}
              value={
                isConnectChecked ? formik?.values?.workStream_id || "" : ""
              }
              onChange={(e) => {
                console.log(e.target.value);
                formik.setFieldValue("workStream_id", e.target.value);
              }}
              disabled={!isConnectChecked}
              error={isConnectChecked && formik.touched.workStream_id && formik.errors.workStream_id}
            />
          </div>
        </div>
      )}
    </div>
  );
};

const NeedMoreHelp = () => {
  const [more, setMore] = useState(false);
  const handleMore = () => setMore(!more);
  return (
    <div className="secondary-box p-4 rounded-4 mt-4 ">
      <div className="d-flex align-items-center justify-content-between gap-2">
        <div className="d-flex gap-2 align-items-center text-secondary">
          <WarningIcon />
          <p className="p4 fw-medium mb-0">Need more help?</p>
        </div>
        <span style={{ cursor: "pointer" }} className="p4" onClick={handleMore}>
          {more ? "Less" : "More"}
        </span>
      </div>

      {more && (
        <div className="mt-3">
          <p>
            <span className="fw-semibold text-muted">Journal Guidelines:</span>{" "}
            Each Journal has specific formatting requirements.Your manuscript
            will be checked against these guidelines.
          </p>
          <p>
            <span className="fw-semibold text-muted">Manuscript Types:</span>{" "}
            Different paper types have different structural requirements and
            word limits.
          </p>
        </div>
      )}
    </div>
  );
};
